import React, { useEffect, useState } from 'react';
import { 
  Box, IconButton, Tooltip, Chip, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText
} from '@mui/material';
import { format } from 'date-fns';
import Datatable from '../../../components/Datatable';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useAuth } from '../../../components/AuthProvider';
import { useLoading } from '../../../components/LoadingProvider';
import api from '../../../services/api';

// Icons
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';

import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

// Components
import DomainForm from './DomainForm';
import { ToggleOff, ToggleOn } from '@mui/icons-material';
import Swal from 'sweetalert2';

export default function AllDomainsTable({ refresh, onRefresh }) {
  const notify = useNotify();
  const auth = useAuth();
  const loading = useLoading();
  const { isLoading, setLoading } = loading;
  
  const [formState, setFormState] = useState({
    open: false,
    id: null
  });
  const [domains, setCategories] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState({
    open: false,
    name: '',
    info: null
  });

  // Carregar categorias
  const fetchCategories = async () => {
    try {
      const response = await api.post('/dns/domains', {
        params: {
          limit: 100, // Solicitar bastante categorias para o dropdown
          offset: 0,
          sort_field: 'name',
          sort_direction: 'asc'
        }
      });
      if (response.data) {
        console.log(response.data);
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  };

  // Carregar dados ao iniciar
  // useEffect(() => {
  //   fetchCategories();
  // }, [refresh]);

  // Função para mostrar info da categoria
  const showCategoryInfo = (name, info) => {
    setCategoryInfo({
      open: true,
      name,
      info
    });
  };

  // Função para fechar detalhes da categoria
  const closeCategoryInfo = () => {
    setCategoryInfo({
      open: false,
      name: '',
      info: null
    });
  };

  // Função para ativar/desativar domínio
  const toggleDomainActive = async (domainId, currentStatus) => {
    try {
      setLoading(true);
      const response = await api.put(`/dns/blacklist/domains/${domainId}`, {
        is_active: !currentStatus
      });
      
      if (response.data.success) {
        notify.setNotify({
          open: true,
          message: response.data.success,
          severity: 'success'
        });
        
        // Exibir resultado dos nós DNS, se houver
        if (response.data.nodes && response.data.nodes.length > 0) {
          const hasErrors = response.data.nodes.some(node => !node.result);
          const nodesMessage = response.data.nodes
            .map(node => `${node.name}: ${node.result ? 'Sucesso' : 'Falha'}`)
            .join('\n');
          
          notify.setNotify({
            open: true,
            message: nodesMessage,
            severity: hasErrors ? 'warning' : 'success'
          });
        }
        
        // Recarregar os dados
        if (onRefresh) onRefresh();
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao atualizar status do domínio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir um domínio
  const deleteDomain = async (domain,category) => {
    try {
      setLoading(true);
      const response = await api.delete(`/dns/blacklist/categories/${category}/domains/${domain}`);
      
      if (response.data.nodes) {
        const hasErrors = response.data.nodes.some(node => !node.result);
        const nodesMessage = response.data.nodes
          .map(node => `${node.name}: ${node.result ? 'Sucesso' : 'Falha'}`)
          .join('\n');
        
        notify.setNotify({
          open: true,
          message: response.data.message || nodesMessage,
          severity: hasErrors ? 'warning' : 'success'
        });
        
        // Recarregar os dados
        if (onRefresh) onRefresh();
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir domínio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir múltiplos domínios
  const deleteMultipleDomains = async (domains) => {
    try {
      setLoading(true);
      const response = await api.post('/dns/blacklist/domains/delete', {
        blacklist: domains
      });
      
      if (response.data.nodes) {
        const hasErrors = response.data.nodes.some(node => !node.result);
        
        notify.setNotify({
          open: true,
          message: response.data.message || `${domains.length} domínios removidos com sucesso.`,
          severity: hasErrors ? 'warning' : 'success'
        });
        
        // Recarregar os dados
        if (onRefresh) onRefresh();
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir domínios',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const addDomains = async (formData) => {
    try {
      setLoading(true);
      
      // Verificar se temos algum conteúdo (texto ou arquivo)
      const hasBlacklist = formData.get('blacklist') && formData.get('blacklist').trim() !== '';
      const hasFile = formData.get('file') !== null;
      
      if (!hasBlacklist && !hasFile) {
        notify.setNotify({
          open: true,
          message: 'Nenhum domínio ou arquivo informado',
          severity: 'warning'
        });
        return;
      }
      
      // Enviar FormData diretamente
      const response = await api.post('/dns/blacklist/domains/create', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log(response.data);
      
      if (response.data.added_domains >= 0) {
        notify.setNotify({
          open: true,
          message: response.data.message || `${response.data.added_domains} domínios adicionados com sucesso`,
          severity: 'success'
        });
        
        // Fechar o formulário
        setFormState({ open: false, id: null });
        
        // Recarregar os dados
        if (onRefresh) onRefresh();
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Erro ao adicionar domínios:", error);
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao adicionar domínios',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Form de domínios */}
      <DomainForm
        open={formState.open}
        id={formState.id}
        categories={domains}
        onClose={() => setFormState({ open: false, id: null })}
        onSubmit={addDomains}
      />
      
      {/* Diálogo de detalhes da categoria */}
      <Dialog
        open={categoryInfo.open}
        onClose={closeCategoryInfo}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Detalhes da Categoria: {categoryInfo.name}</DialogTitle>
        <DialogContent>
          {categoryInfo.info && (
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Status" 
                  secondary={categoryInfo.info.is_active ? "Ativa" : "Inativa"} 
                />
                <Chip 
                  size="small" 
                  color={categoryInfo.info.is_active ? "success" : "default"}
                  label={categoryInfo.info.is_active ? "Ativa" : "Inativa"}
                />
              </ListItem>
              {categoryInfo.info.expired_at && (
                <ListItem>
                  <ListItemText 
                    primary="Data de Expiração" 
                    secondary={format(new Date(categoryInfo.info.expired_at), 'dd/MM/yyyy')} 
                  />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCategoryInfo}>Fechar</Button>
        </DialogActions>
      </Dialog>
      
      {/* Tabela de domínios */}
      <Datatable
        refresh={refresh}
        handlerRefresh={onRefresh}
        title="Todos os Domínios Bloqueados"
        endpoint="/dns/blacklist/domains"
        options={{
          customToolbar: () =>
            <>
              <Tooltip title="Remover todos os filtros">
                <IconButton onClick={() => { localStorage.removeItem("datatable/dns/blacklist/domains"); window.location.reload() }} >
                  <RemoveCircleOutline />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Adicionar Domínios">
                <IconButton 
                  disabled={!auth.hasPerm("dns_blacklist.create")}
                  onClick={() => setFormState({ open: true, id: null })}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip> */}
            </>
          ,
          selectableRows: "multiple",
          customToolbarSelect: (selectedRows, displayData, setSelectedRows) => 
            <Tooltip title="Remover domínios selecionados">
              <IconButton 
                disabled={!auth.hasPerm('dns_blacklist.delete')}
                onClick={() => {
                  const selectedDomains = selectedRows.data.map((row) => displayData[row.dataIndex].data[1]);
                  
                  if (window.confirm('Deseja remover os domínios selecionados?')) {
                    deleteMultipleDomains(selectedDomains);
                    setSelectedRows([]);
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
        }}
        columns={[
          {
            name: 'id',
            label: 'ID',
            options: {
              display: false
            }
          },
          {
            name: 'domain',
            label: 'Domínio',
          },
          {
            name: 'category',
            label: 'Categoria',
            options: {
              customBodyRender: (value, tableMeta) => {
                const isActive = tableMeta.rowData[3]; // campo "status"
                return (
                  <Chip
                  label={value}
                  size="small"
                  variant="outlined"
                  color={isActive ? "primary" : "error"}
                  />
                );
                }
              }
              },
              {
              name: 'status',
              label: 'Status',
              options: {
                customBodyRender: (value) => (
                <Chip 
                  label={value === true ? 'Ativo' : 'Inativo'}
                  size="small"
                  color={value === true ? 'success' : 'default'}
                />
                )
              }
              },
              {
              name: 'dns_list',
              label: 'Ativo',
              options: {
                display: false
              }
              },
              {
              name: 'actions',
              label: 'Ações',
              options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                const domainId = tableMeta.rowData[0];
                const domain = tableMeta.rowData[1];
                const isActive = tableMeta.rowData[5];
                const categoryId = tableMeta.rowData[4];
                
                return (
                  <Box>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        disabled={!auth.hasPerm("dns_blacklist.delete")}
                        onClick={() => {
                          Swal.fire({
                            title: 'Tem certeza?',
                            text: "Você não poderá reverter isso!",
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Sim, excluir!',
                            cancelButtonText: 'Cancelar'
                          }).then((result) => {
                            if (result.isConfirmed) {
                              deleteDomain(domainId, categoryId);
                            }
                          });
                         
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              }
            }
          }
        ]}
      />
    </>
  );
}